import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ApplicationData, REGISTRANT_OPTIONS } from 'src/types/flow';
import { ExpressRegistrationRegistrant } from 'src/types/configuration';
import { FormModelPerson } from 'src/types/person';
import {
  getInitialFormModelValues,
  initialPerson,
  initializeRegistrantFormState,
  mapOwnersToFormModel,
  userDetailsToPerson,
} from 'src/common/people';
import { useUserRole } from 'src/hooks/useUserRole';
import { selectCurrentUser } from 'src/store/slices/auth/authSelectors';
import { selectConfigExpressRegistrationRegistrantPhysicalAddress } from 'src/store/slices/config/configSelectors';
export interface RegistrantFormRef {
  getValues: () => ApplicationData;
  validateForm: () => boolean;
}

interface UseRegistrantFormProps {
  allowRoleChange?: boolean;
  data: ApplicationData;
  isAdminUpdate?: boolean;
  multipleOwners?: boolean;
  registrantOptions: ExpressRegistrationRegistrant;
}

export const useRegistrantForm = ({
  allowRoleChange,
  data,
  isAdminUpdate,
  multipleOwners,
  registrantOptions,
}: UseRegistrantFormProps) => {
  const [hasUnselectedOwnerRadioError, setHasUnselectedOwnerRadioError] =
    useState(false);

  const {
    allow_owner_editing,
    property_manager,
    property_manager_independent,
  } = registrantOptions;

  const currentUser = useSelector(selectCurrentUser);
  const registrantPhysicalAddressConfig = useSelector(
    selectConfigExpressRegistrationRegistrantPhysicalAddress
  );
  const showPhysicalAddress = useMemo(
    () => !!registrantPhysicalAddressConfig?.required,
    [registrantPhysicalAddressConfig]
  );
  const {
    localRole: initialLocalRole,
    owners: initialOwners,
    propertyManager: initialPropertyManager,
  } = initializeRegistrantFormState(
    data,
    currentUser ? userDetailsToPerson(currentUser) : initialPerson
  );

  const [owners, setOwners] = useState<FormModelPerson[]>(initialOwners);
  const [localRole, setLocalRole] =
    useState<REGISTRANT_OPTIONS>(initialLocalRole);

  const { registrant, contacts, property, tenant } = data;

  const {
    isOwner,
    isPropertyManager,
    isRegistrant,
    isTenant,
    selectedRegistrantOption,
  } = useUserRole(registrant, contacts, tenant);

  const effectiveOption = useMemo(
    () => (allowRoleChange ? localRole : selectedRegistrantOption),
    [allowRoleChange, localRole, selectedRegistrantOption]
  );

  const showPropertyManagerForm = useMemo(() => {
    if (allowRoleChange && property_manager && property_manager_independent) {
      return (
        effectiveOption === REGISTRANT_OPTIONS.PROPERTY_MANAGER ||
        effectiveOption === REGISTRANT_OPTIONS.PROPERTY_MANAGER_INDEPENDENT
      );
    }
    return false;
  }, [
    allowRoleChange,
    effectiveOption,
    property_manager,
    property_manager_independent,
  ]);

  const showOwnersForm = useMemo(() => {
    if (!effectiveOption) {
      return false;
    }
    return (
      (effectiveOption === REGISTRANT_OPTIONS.OWNER && !!multipleOwners) ||
      effectiveOption !== REGISTRANT_OPTIONS.OWNER
    );
  }, [effectiveOption, multipleOwners]);

  useEffect(() => {
    if (isPropertyManager || isTenant) {
      const newOwners = mapOwnersToFormModel(
        registrant?.owner ?? [],
        property?.propertyaddress
      );
      setOwners(newOwners);
    }
  }, [registrant, property, isPropertyManager, isTenant]);

  const savedNonOwnerOwners = useRef<FormModelPerson[]>([]);
  const prevLocalRole = useRef<REGISTRANT_OPTIONS | undefined>(localRole);

  useEffect(() => {
    if (!allowRoleChange) return;
    if (localRole === REGISTRANT_OPTIONS.OWNER) {
      setOwners((prev) => {
        savedNonOwnerOwners.current = prev;
        return [];
      });
    } else if (prevLocalRole.current === REGISTRANT_OPTIONS.OWNER) {
      setOwners(
        savedNonOwnerOwners.current.length > 0
          ? savedNonOwnerOwners.current
          : [getInitialFormModelValues()]
      );
    }
    prevLocalRole.current = localRole;
  }, [allowRoleChange, localRole]);

  const canChangePartialOwnerDetails = useMemo(() => {
    if (!allowRoleChange) {
      return isOwner || isAdminUpdate || (isPropertyManager && isRegistrant);
    }
    return false;
  }, [
    allowRoleChange,
    isAdminUpdate,
    isOwner,
    isPropertyManager,
    isRegistrant,
  ]);

  const canChangeAllOwnerDetails = useMemo(() => {
    if (allowRoleChange)
      return (
        (!!allow_owner_editing && isPropertyManager && isRegistrant) ||
        isAdminUpdate
      );
    return true;
  }, [
    allow_owner_editing,
    allowRoleChange,
    isAdminUpdate,
    isPropertyManager,
    isRegistrant,
  ]);

  const handleRoleChange = useCallback(
    (value: REGISTRANT_OPTIONS) => {
      if (allowRoleChange) {
        setHasUnselectedOwnerRadioError(false);
        setLocalRole(value);
      }
    },
    [allowRoleChange]
  );

  return {
    canChangeAllOwnerDetails,
    canChangePartialOwnerDetails,
    currentUser,
    effectiveOption,
    handleRoleChange,
    hasUnselectedOwnerRadioError,
    initialPropertyManager,
    owners,
    property,
    registrant,
    setHasUnselectedOwnerRadioError,
    setOwners,
    showOwnersForm,
    showPhysicalAddress,
    showPropertyManagerForm,
  };
};
